import * as React from "react";
import { Typography } from "@mui/material";
import axios from "axios";
import "./lotteryUpdateMarquee.css";

interface LotteryUpdateMarqueeProps {
  feedUrl?: string;
  interval?: number;
  position?: "header" | "footer";
  backendEndpoint: string;
}

interface ParsedGame {
  gameType: string;
  title: string;
  numbers: string[];
  bonusNumber?: string;
  jackpot?: string;
  drawDate: string;
  description: string;
  isJackpot?: boolean;
  powerPlay?: string;
  megaplier?: string;
}

const LotteryUpdateMarquee: React.FC<LotteryUpdateMarqueeProps> = ({
  interval = 900000,
  position = "header",
  backendEndpoint,
}) => {
  const [pick3Draws, setPick3Draws] = React.useState<ParsedGame[]>([]);
  const [daily4Draws, setDaily4Draws] = React.useState<ParsedGame[]>([]);
  const [texasTwoStep, setTexasTwoStep] = React.useState<ParsedGame | null>(null);
  const [cashFive, setCashFive] = React.useState<ParsedGame | null>(null);
  const [powerball, setPowerball] = React.useState<ParsedGame | null>(null);
  const [megaMillions, setMegaMillions] = React.useState<ParsedGame | null>(null);
  const [lottoTexas, setLottoTexas] = React.useState<ParsedGame | null>(null);
  const [allOrNothingDraws, setAllOrNothingDraws] = React.useState<ParsedGame[]>([]);
  const [error, setError] = React.useState<string>("");
  const trackRef = React.useRef<HTMLDivElement | null>(null);
  const offsetRef = React.useRef<number>(0);

  // ─── Parsers ──────────────────────────────────────────────────────────────

  const parseNumbers = (description: string): {
    numbers: string[];
    bonus?: string;
    fireball?: string;
    powerPlay?: string;
    megaplier?: string;
  } => {
    const cleaned = description.trim().replace(/\s+/g, " ");
    const dashSeparated = cleaned.split(" - ");
    const numbers: string[] = [];
    let bonus: string | undefined;
    let fireball: string | undefined;
    let powerPlay: string | undefined;
    let megaplier: string | undefined;

    for (let i = 0; i < dashSeparated.length; i++) {
      const trimmed = dashSeparated[i].trim();
      const lower = trimmed.toLowerCase();

      const hasPowerball = lower.includes("powerball");
      const hasMegaball  = lower.includes("megaball");
      const hasBonus     = lower.includes("bonus");
      const hasFireball  = lower.includes("fireball");
      const hasPowerPlay = lower.includes("power play");
      const hasMegaplier = lower.includes("megaplier");

      if (hasPowerball || hasMegaball || hasBonus || hasFireball || hasPowerPlay || hasMegaplier) {
        const numBefore = trimmed.match(/^(\d+)(?:\s+|)(powerball|megaball|bonus|fireball|power\s*play|megaplier|fi)/i);
        if (numBefore) {
          numbers.push(numBefore[1]);
        } else {
          const numStart = trimmed.match(/^(\d+)/);
          if (numStart && (hasFireball || lower.includes("fi"))) {
            numbers.push(numStart[1]);
          }
        }
        // Capture bonus / special balls and multipliers. These are independent so a line
        // can contain both a Powerball and a Power Play value.
        if (hasPowerball) {
          const m = trimmed.match(/powerball\s*(\d+)/i);
          if (m) bonus = m[1];
        }
        if (hasMegaball) {
          const m = trimmed.match(/megaball\s*(\d+)/i);
          if (m) bonus = m[1];
        }
        if (hasBonus) {
          const m = trimmed.match(/bonus\s*ball\s*(\d+)/i);
          if (m) bonus = m[1];
        }
        if (hasFireball) {
          const m = trimmed.match(/fireball\s*(\d+)/i);
          if (m) fireball = m[1];
        }
        if (hasPowerPlay) {
          // Handles formats like "Power Play 4" and "Power Play X 4"
          const m = trimmed.match(/power\s*play(?:\s*[xX])?\s*(\d+)/i);
          if (m) powerPlay = m[1];
        }
        if (hasMegaplier) {
          const m = trimmed.match(/megaplier\s*(\d+)/i);
          if (m) megaplier = m[1];
        }
      } else if (/^\d+$/.test(trimmed)) {
        numbers.push(trimmed);
      }
    }

    if (numbers.length === 0) {
      const parts = cleaned.split(" ");
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        if (/^\d+$/.test(part)) {
          const next = i + 1 < parts.length ? parts[i + 1].toLowerCase() : "";
          if (next.includes("powerball") || next.includes("megaball") ||
              next.includes("bonus") || next.includes("fireball") ||
              next.includes("power") || next.includes("megaplier")) {
            numbers.push(part);
            if (i + 2 < parts.length && /^\d+$/.test(parts[i + 2])) {
              if (next.includes("fireball")) fireball = parts[i + 2];
              else bonus = parts[i + 2];
              i += 2;
            } else { i++; }
          } else {
            numbers.push(part);
          }
        } else if (part.toLowerCase().includes("powerball") || part.toLowerCase().includes("megaball")) {
          if (i + 1 < parts.length && /^\d+$/.test(parts[i + 1])) { bonus = parts[i + 1]; i++; }
        } else if (part.toLowerCase().includes("fireball")) {
          if (i + 1 < parts.length && /^\d+$/.test(parts[i + 1])) { fireball = parts[i + 1]; i++; }
        } else if (part.toLowerCase().includes("bonus")) {
          if (i + 1 < parts.length && /^\d+$/.test(parts[i + 1])) { bonus = parts[i + 1]; i++; }
        }
      }
    }

    return { numbers, bonus, fireball, powerPlay, megaplier };
  };

  const parseGameType = (title: string): string => {
    const t = title.toLowerCase();
    if (t.includes("powerball"))      return "Powerball";
    if (t.includes("mega millions"))  return "Mega Millions";
    if (t.includes("lotto texas"))    return "Lotto Texas";
    if (t.includes("texas two step")) return "Texas Two Step";
    if (t.includes("cash five"))      return "Cash Five";
    if (t.includes("pick 3"))         return "Pick 3";
    if (t.includes("daily 4"))        return "Daily 4";
    if (t.includes("all or nothing")) return "All or Nothing";
    return "Unknown";
  };

  const extractDrawTime = (title: string): string => {
    const t = title.toLowerCase();
    if (t.includes("morning")) return "Morning";
    if (t.includes("day"))     return "Day";
    if (t.includes("evening")) return "Evening";
    if (t.includes("night"))   return "Night";
    return "";
  };

  const extractDate = (title: string): string => {
    const m = title.match(/(\d{2}\/\d{2}\/\d{4})/);
    return m ? m[1] : "";
  };

  const parseJackpot = (description: string): string | undefined => {
    const m = description.match(/(\$[\d,\.]+(?:\s*(?:Million|Billion))?)/i);
    if (m) return m[1];
    const a = description.match(/Annuitized:\s*(\$[\d,\.]+(?:\s*(?:Million|Billion))?)/i);
    if (a) return a[1];
    return undefined;
  };

  // ─── Fetch ────────────────────────────────────────────────────────────────

  const fetchFeed = async () => {
    if (!backendEndpoint) { setError("Backend endpoint not configured"); return; }

    try {
      const response = await axios.get(backendEndpoint, {
        headers: { "Accept": "application/json" },
        timeout: 15000,
      });

      if (response.data?.success && response.data?.data) {
        const items: any[] = response.data.data.items || [];

        const pick3BySlot:  { [slot: string]: ParsedGame } = {};
        const daily4BySlot: { [slot: string]: ParsedGame } = {};
        let latestTwoStep:   ParsedGame | null = null;
        let latestCashFive:  ParsedGame | null = null;
        let latestPowerball:   ParsedGame | null = null;
        let latestMegaMillions: ParsedGame | null = null;
        let latestLottoTexas:  ParsedGame | null = null;
        const allOrNothingBySlot: { [slot: string]: ParsedGame } = {};
        let twoStepJackpot:   string | undefined;
        let cashFiveTopPrize: string | undefined;
        let powerballJackpot: string | undefined;
        let megaMillionsJackpot: string | undefined;
        let lottoTexasJackpot: string | undefined;
        let allOrNothingTopPrize: string | undefined;

        // First pass: find jackpot/prize items (not winning-numbers items)
        items.forEach((item) => {
          const title   = (item.title || "").toLowerCase();
          const content = item.contentSnippet || item.content || item.description || "";
          if (title.includes("texas two step") && title.includes("advertised jackpot")) {
            twoStepJackpot = parseJackpot(content) || content.trim() || undefined;
          }
          if (title.includes("cash five") && (title.includes("top prize") || title.includes("jackpot"))) {
            cashFiveTopPrize = parseJackpot(content) || content.trim() || undefined;
          }
          if (title.includes("powerball") && title.includes("estimated jackpot")) {
            powerballJackpot = parseJackpot(content) || content.trim() || undefined;
          }
          if (title.includes("mega millions") && title.includes("estimated")) {
            megaMillionsJackpot = parseJackpot(content) || content.trim() || undefined;
          }
          if (title.includes("lotto texas") && title.includes("estimated jackpot")) {
            lottoTexasJackpot = parseJackpot(content) || content.trim() || undefined;
          }
          if (title.includes("all or nothing") && (title.includes("top prize") || title.includes("jackpot") || title.includes("estimated"))) {
            allOrNothingTopPrize = parseJackpot(content) || content.trim() || undefined;
          }
        });

        // Second pass: winning numbers for all games
        items.forEach((item) => {
          const title       = item.title || "";
          const description = item.contentSnippet || item.content || item.description || "";
          const gameType    = parseGameType(title);

          if (!title.toLowerCase().includes("winning numbers")) return;

          const { numbers, fireball, bonus, powerPlay, megaplier } = parseNumbers(description);
          const drawDate = extractDate(title);

          // ── Pick 3 ──
          if (gameType === "Pick 3") {
            const drawTime = extractDrawTime(title);
            if (numbers.length > 0 && drawTime && !pick3BySlot[drawTime]) {
              pick3BySlot[drawTime] = {
                gameType, title: `Pick 3 ${drawTime}`,
                numbers, bonusNumber: fireball, drawDate, description,
              };
            }
          }

          // ── Daily 4 ──
          if (gameType === "Daily 4") {
            const drawTime = extractDrawTime(title);
            if (numbers.length > 0 && drawTime && !daily4BySlot[drawTime]) {
              daily4BySlot[drawTime] = {
                gameType, title: `Daily 4 ${drawTime}`,
                numbers, bonusNumber: fireball, drawDate, description,
              };
            }
          }

          // ── Texas Two Step ──
          if (gameType === "Texas Two Step" && numbers.length > 0 && !latestTwoStep) {
            latestTwoStep = {
              gameType, title, numbers,
              bonusNumber: bonus,
              jackpot: twoStepJackpot,
              drawDate, description,
            };
          }

          // ── Cash Five ──
          if (gameType === "Cash Five" && numbers.length > 0 && !latestCashFive) {
            latestCashFive = {
              gameType, title, numbers,
              jackpot: cashFiveTopPrize,
              drawDate, description,
            };
          }

          // ── Powerball ──
          if (gameType === "Powerball" && numbers.length > 0 && !latestPowerball) {
            latestPowerball = {
              gameType, title, numbers,
              bonusNumber: bonus,
              jackpot: powerballJackpot,
              powerPlay: powerPlay || undefined,
              drawDate, description,
            };
          }

          // ── Mega Millions ──
          if (gameType === "Mega Millions" && numbers.length > 0 && !latestMegaMillions) {
            latestMegaMillions = {
              gameType, title, numbers,
              bonusNumber: bonus,
              jackpot: megaMillionsJackpot,
              megaplier: megaplier || undefined,
              drawDate, description,
            };
          }

          // ── Lotto Texas (Extra) ──
          if (gameType === "Lotto Texas" && numbers.length > 0 && !latestLottoTexas) {
            latestLottoTexas = {
              gameType, title, numbers,
              jackpot: lottoTexasJackpot,
              drawDate, description,
            };
          }

          // ── All or Nothing (time slots like Pick 3, 12 numbers per draw) ──
          if (gameType === "All or Nothing") {
            const drawTime = extractDrawTime(title);
            if (numbers.length > 0 && drawTime && !allOrNothingBySlot[drawTime]) {
              allOrNothingBySlot[drawTime] = {
                gameType, title: `All or Nothing ${drawTime}`,
                numbers, drawDate, description,
                jackpot: allOrNothingTopPrize,
              };
            }
          }
        });

        setPick3Draws(Object.values(pick3BySlot));
        setDaily4Draws(Object.values(daily4BySlot));
        setTexasTwoStep(latestTwoStep);
        setCashFive(latestCashFive);
        setPowerball(latestPowerball);
        setMegaMillions(latestMegaMillions);
        setLottoTexas(latestLottoTexas);
        setAllOrNothingDraws(Object.values(allOrNothingBySlot));
        setError("");
      } else {
        throw new Error("Invalid response format from backend");
      }
    } catch (err: any) {
      console.error("Backend endpoint failed:", err);
      setError("Failed to fetch lottery updates. Please try again later.");
    }
  };

  React.useEffect(() => {
    fetchFeed();
    const id = setInterval(fetchFeed, interval);
    return () => clearInterval(id);
  }, [backendEndpoint, interval]);

  // Smooth marquee animation driven by requestAnimationFrame so it doesn't
  // visibly jump when the RSS feed refreshes and content rerenders.
  React.useEffect(() => {
    const speed = 40; // pixels per second
    let animationFrameId: number;
    let lastTime = performance.now();

    const step = (time: number) => {
      const dt = (time - lastTime) / 1000;
      lastTime = time;

      const track = trackRef.current;
      if (track) {
        const singleWidth = track.scrollWidth / 2 || 0; // width of one full set of cards
        if (singleWidth > 0) {
          offsetRef.current -= speed * dt;
          if (offsetRef.current <= -singleWidth) {
            offsetRef.current += singleWidth;
          }
          track.style.transform = `translate3d(${offsetRef.current}px, 0, 0)`;
        }
      }

      animationFrameId = window.requestAnimationFrame(step);
    };

    animationFrameId = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(animationFrameId);
  }, []);

  // ─── Renderers ────────────────────────────────────────────────────────────

  /** Shared card for Pick 3 and Daily 4 (time-slot layout) */
  const renderTimeSlotCard = (
    games: ParsedGame[],
    cssPrefix: string,
    logoSrc: string,
    schedule: string,
    placeholderCount: number,
  ) => {
    const timeOrder = ["Morning", "Day", "Evening", "Night"];
    const slots = timeOrder.map((time) =>
      games.find((g) => extractDrawTime(g.title) === time) || null
    );
    const placeholders = Array.from({ length: placeholderCount }, (_, i) => i);

    return (
      <div className={`${cssPrefix}-card`}>
        <div className={`${cssPrefix}-card__inner`}>
          {/* Logo section */}
          <div className={`${cssPrefix}-card__logo`}>
            <img src={logoSrc} alt={cssPrefix} className={`${cssPrefix}-card__logo-img`} />
            <span className={`${cssPrefix}-card__logo-days`}>{schedule}</span>
          </div>

          {/* One column per time slot */}
          {slots.map((game, idx) => (
            <div key={idx} className={`${cssPrefix}-card__col`}>
              <span className={`${cssPrefix}-card__col-title`}>
                Results for {timeOrder[idx]}
              </span>
              <span className={`${cssPrefix}-card__col-date`}>
                Draw {game?.drawDate || "—"}
              </span>
              <div className={`${cssPrefix}-card__col-numbers`}>
                {game
                  ? game.numbers.map((num, ni) => (
                      <span key={ni} className={`${cssPrefix}-card__ball`}>{num}</span>
                    ))
                  : placeholders.map((i) => (
                      <span key={i} className={`${cssPrefix}-card__ball`}>-</span>
                    ))}
              </div>
              <div className={`${cssPrefix}-card__fireball`}>
                <span className={`${cssPrefix}-card__fireball-text`}>FIREBALL</span>
                <span className={`${cssPrefix}-card__fireball-num`}>
                  {game?.bonusNumber || "-"}
                  <span className={`${cssPrefix}-card__fireball-icon`}>🔥</span>
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderPick3Card  = (games: ParsedGame[]) =>
    renderTimeSlotCard(games, "pick3",  "/pick3Game.png",  "MON - SAT", 3);

  const renderDaily4Card = (games: ParsedGame[]) =>
    renderTimeSlotCard(games, "daily4", "/daily4Game.png", "MON - SAT", 4);

  /** Shared card for single-draw jackpot games (Texas Two Step, Cash Five) */
  const renderJackpotCard = (
    game: ParsedGame,
    cssPrefix: string,
    logoSrc: string,
    logoAlt: string,
    schedule: string,
    prizeLabel: string,
    prizeTitle: string,
    ballCount: number,
    hasBonusBall: boolean,
    prizeAmountOverride?: string,
  ) => {
    const mainNumbers = game.numbers.slice(0, ballCount);
    const bonusBall   = hasBonusBall
      ? (game.bonusNumber || game.numbers[ballCount] || null)
      : null;
    const prizeAmount = prizeAmountOverride ?? game.jackpot ?? "—";

    return (
      <div className={`${cssPrefix}-card`}>
        <div className={`${cssPrefix}-card__inner`}>
          {/* Logo */}
          <div className={`${cssPrefix}-card__logo`}>
            <img src={logoSrc} alt={logoAlt} className={`${cssPrefix}-card__logo-img`} />
            <span className={`${cssPrefix}-card__logo-days`}>{schedule}</span>
          </div>

          {/* Prize panel */}
          <div className={`${cssPrefix}-card__prize`}>
            <span className={`${cssPrefix}-card__prize-label`}>{prizeLabel}</span>
            <span className={`${cssPrefix}-card__prize-title`}>{prizeTitle}</span>
            <span className={`${cssPrefix}-card__prize-amount`}>{prizeAmount}</span>
          </div>

          {/* Winning Numbers */}
          <div className={`${cssPrefix}-card__numbers`}>
            <span className={`${cssPrefix}-card__numbers-title`}>Winning Numbers</span>
            <span className={`${cssPrefix}-card__numbers-date`}>for {game.drawDate}</span>
            <div className={`${cssPrefix}-card__numbers-row`}>
              {mainNumbers.map((num, idx) => (
                <span key={idx} className={`${cssPrefix}-card__ball`}>{num}</span>
              ))}
              {bonusBall && (
                <span className={`${cssPrefix}-card__ball ${cssPrefix}-card__ball--bonus`}>
                  {bonusBall}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTexasTwoStepCard = (game: ParsedGame) =>
    renderJackpotCard(game, "tts", "/texasTwoStepGame.png", "Texas Two Step",
      "MON | THU", "Current Advertised.", "Jackpot", 4, true);

  const renderCashFiveCard = (game: ParsedGame) =>
    renderJackpotCard(game, "cf", "/cashFiveGame.png", "Cash Five",
      "MON | SAT", "", "Top Prize", 5, false, "$25,000");

  /** Powerball card: logo | jackpot panel | winning numbers + power play */
  const renderPowerballCard = (game: ParsedGame) => {
    const mainNumbers = game.numbers.slice(0, 5);
    const bonusBall   = game.bonusNumber || game.numbers[5] || null;

    return (
      <div className="pb-card">
        <div className="pb-card__inner">
          {/* Logo */}
          <div className="pb-card__logo">
            <img src="/powerballGame.png" alt="Powerball" className="pb-card__logo-img" />
            <span className="pb-card__logo-days">MON | WED | SAT</span>
          </div>

          {/* Jackpot panel */}
          <div className="pb-card__prize">
            <span className="pb-card__prize-label">Current Est.</span>
            <span className="pb-card__prize-title">Jackpot</span>
            <span className="pb-card__prize-amount">{game.jackpot || "—"}</span>
          </div>

          {/* Winning Numbers */}
          <div className="pb-card__numbers">
            <span className="pb-card__numbers-title">Winning Numbers</span>
            <span className="pb-card__numbers-date">for {game.drawDate}</span>
            <div className="pb-card__numbers-row">
              {mainNumbers.map((num, idx) => (
                <span key={idx} className="pb-card__ball">{num}</span>
              ))}
              {bonusBall && (
                <span className="pb-card__ball pb-card__ball--bonus">{bonusBall}</span>
              )}
            </div>
            {game.powerPlay && (
              <span className="pb-card__power-play">Power Play X {game.powerPlay}</span>


            )}
          </div>
        </div>
      </div>
    );
  };

  /** Mega Millions card: logo | jackpot (dark blue, yellow text) | winning numbers + mega ball (yellow) + megaplier */
  const renderMegaMillionsCard = (game: ParsedGame) => {
    const mainNumbers = game.numbers.slice(0, 5);
    const megaBall = game.bonusNumber || game.numbers[5] || null;

    return (
      <div className="mm-card">
        <div className="mm-card__inner">
          <div className="mm-card__logo">
            <img src="/megaMillionsGame.png" alt="Mega Millions" className="mm-card__logo-img" />
            <span className="mm-card__logo-days">TUE | FRI</span>
          </div>

          <div className="mm-card__prize">
            <span className="mm-card__prize-label">Current Est.</span>
            <span className="mm-card__prize-title">Jackpot</span>
            <span className="mm-card__prize-amount">{game.jackpot || "—"}</span>
          </div>

          <div className="mm-card__numbers">
            <span className="mm-card__numbers-title">Winning Numbers</span>
            <span className="mm-card__numbers-date">for {game.drawDate}</span>
            <div className="mm-card__numbers-row">
              {mainNumbers.map((num, idx) => (
                <span key={idx} className="mm-card__ball">{num}</span>
              ))}
              {megaBall && (
                <span className="mm-card__ball mm-card__ball--mega">{megaBall}</span>
              )}
            </div>
            {game.megaplier && (
              <span className="mm-card__megaplier">Megaplier: {game.megaplier}</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  /** Lotto Texas Extra: logo | jackpot (#C90047, yellow text) | 6 winning numbers (no bonus styling) */
  const renderLottoTexasCard = (game: ParsedGame) => {
    const numbers = game.numbers.slice(0, 6);

    return (
      <div className="lt-card">
        <div className="lt-card__inner">
          <div className="lt-card__logo">
            <img src="/lottoTexasGame.png" alt="Lotto Texas Extra" className="lt-card__logo-img" />
            <span className="lt-card__logo-days">MON | WED | SAT</span>
          </div>

          <div className="lt-card__prize">
            <span className="lt-card__prize-label">Current Est.</span>
            <span className="lt-card__prize-title">Jackpot</span>
            <span className="lt-card__prize-amount">{game.jackpot || "—"}</span>
          </div>

          <div className="lt-card__numbers">
            <span className="lt-card__numbers-title">Winning Numbers</span>
            <span className="lt-card__numbers-date">for {game.drawDate}</span>
            <div className="lt-card__numbers-row">
              {numbers.map((num, idx) => (
                <span key={idx} className="lt-card__ball">{num}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  /** All or Nothing: logo | Top Prize (#DB8700) | 4 time slots, 12 numbers each (2 rows of 6) */
  const renderAllOrNothingCard = (games: ParsedGame[]) => {
    const timeOrder = ["Morning", "Day", "Evening", "Night"];
    const slots = timeOrder.map((time) =>
      games.find((g) => extractDrawTime(g.title) === time) || null
    );
    const NUMBERS_PER_SLOT = 12;

    return (
      <div className="aon-card">
        <div className="aon-card__inner">
          <div className="aon-card__logo">
            <img src="/allOrNothingGame.png" alt="All or Nothing" className="aon-card__logo-img" />
            <span className="aon-card__logo-days">MON | SAT</span>
          </div>

          <div className="aon-card__prize">
            <span className="aon-card__prize-title">Top Prize</span>
            <span className="aon-card__prize-amount">$250,000</span>
          </div>

          {slots.map((game, idx) => {
            const drawTime = timeOrder[idx];
            const numbers = game ? game.numbers.slice(0, NUMBERS_PER_SLOT) : [];
            const placeholders = Array.from({ length: Math.max(0, NUMBERS_PER_SLOT - numbers.length) }, (_, i) => i);
            return (
              <div key={idx} className="aon-card__col">
                <span className="aon-card__col-title">Winning Numbers</span>
                <span className="aon-card__col-date">for {drawTime} {game?.drawDate || ""}</span>
                <div className="aon-card__col-numbers">
                  {numbers.map((num, ni) => (
                    <span key={ni} className="aon-card__ball">{num}</span>
                  ))}
                  {placeholders.map((i) => (
                    <span key={`p-${i}`} className="aon-card__ball">—</span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  if (error) {
    return (
      <div className="lottery-update-error">
        <Typography>{error}</Typography>
      </div>
    );
  }

  const hasData = pick3Draws.length > 0 || daily4Draws.length > 0 || texasTwoStep !== null || cashFive !== null || powerball !== null || megaMillions !== null || lottoTexas !== null || allOrNothingDraws.length > 0;
  if (!hasData) {
    return (
      <div className="lottery-update-loading">
        <Typography>Loading lottery updates...</Typography>
      </div>
    );
  }

  // Render all game cards in client-requested order: Powerball, Mega Millions, Lotto, Texas Two Step, Cash Five, Pick 3, Daily 4, All or Nothing.
  const renderAllCards = () => (
    <>
      {powerball !== null     && renderPowerballCard(powerball)}
      {megaMillions !== null  && renderMegaMillionsCard(megaMillions)}
      {lottoTexas !== null    && renderLottoTexasCard(lottoTexas)}
      {texasTwoStep !== null  && renderTexasTwoStepCard(texasTwoStep)}
      {cashFive !== null      && renderCashFiveCard(cashFive)}
      {pick3Draws.length > 0  && renderPick3Card(pick3Draws)}
      {daily4Draws.length > 0 && renderDaily4Card(daily4Draws)}
      {allOrNothingDraws.length > 0 && renderAllOrNothingCard(allOrNothingDraws)}
    </>
  );

  return (
    <div className={`lottery-update-marquee lottery-update-${position}`}>
      {/* overflow:hidden on container, animated track inside */}
      <div className="lottery-marquee-track" ref={trackRef}>
        {/* Duplicate content so the scroll loops without a gap */}
        <div className="lottery-marquee-content" aria-hidden="false">{renderAllCards()}</div>
        <div className="lottery-marquee-content" aria-hidden="true">{renderAllCards()}</div>
      </div>
    </div>
  );
};

export default LotteryUpdateMarquee;

